/**
 * End-of-shift HOTO notes (officer key -> "on break till 1000, reports to car field at
 * 1000"), typed beside OT officers' rows in the officer graph.
 *
 * Belong to the SESSION, not the browser: saved server side one officer at a time (so two
 * devices typing for different officers never overwrite each other) and carried back on
 * every roster-state payload, so a refresh or another device shows the same notes. Typing
 * shows at once and is saved after a short pause; a note being typed here is never
 * overwritten by an incoming state until its save has gone out. Same shape as
 * utils/officerOrder.
 */
import { useSyncExternalStore } from 'react';
import { apiClient } from '@/api/client';

const SAVE_DELAY_MS = 600;

let notes: Record<string, string> = {};
let loaded = false;
let inFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();
/** Keys typed since their last save went out, with the timer that will send them. */
const pending = new Map<string, ReturnType<typeof setTimeout>>();

function emit() { listeners.forEach(l => l()); }

const sameNotes = (a: Record<string, string>, b: Record<string, string>) => {
  const ak = Object.keys(a);
  return ak.length === Object.keys(b).length && ak.every(k => a[k] === b[k]);
};

export function getHotoNotes(): Record<string, string> { return notes; }

/** Adopt the notes that came back with a roster-state payload (keeps unsent local edits). */
export function adoptHotoNotes(value: Record<string, string> | undefined): void {
  if (value === undefined) return;
  loaded = true;
  const next = { ...value };
  for (const key of pending.keys()) {
    if (key in notes) next[key] = notes[key]; else delete next[key];
  }
  if (sameNotes(next, notes)) return;
  notes = next;
  emit();
}

/** Forget what we know — call when the session changes. */
export function resetHotoNotes(): void {
  pending.forEach(t => clearTimeout(t));
  pending.clear();
  loaded = false;
  inFlight = null;
  if (Object.keys(notes).length) { notes = {}; emit(); }
}

/** Fetch the session's notes once if no state payload has delivered them yet. */
function loadHotoNotes(): Promise<void> {
  if (loaded || inFlight) return inFlight ?? Promise.resolve();
  inFlight = apiClient
    .getHotoNotes()
    .then(({ hoto_notes }) => { adoptHotoNotes(hoto_notes); })
    .catch(() => { /* offline — no notes yet */ })
    .finally(() => { inFlight = null; });
  return inFlight;
}

/** Set one officer's note: shown at once, saved to the session after a short pause
 *  (`onError` runs if that save fails, e.g. in a view-only session). */
export function setHotoNote(officerKey: string, text: string, onError?: () => void): void {
  loaded = true;
  const next = { ...notes };
  if (text) next[officerKey] = text; else delete next[officerKey];
  notes = next;
  emit();

  const timer = pending.get(officerKey);
  if (timer) clearTimeout(timer);
  pending.set(officerKey, setTimeout(() => {
    void apiClient
      .setHotoNote(officerKey, notes[officerKey] ?? '')
      .catch(() => onError?.())
      .finally(() => { pending.delete(officerKey); });
  }, SAVE_DELAY_MS));
}

/** Re-renders the caller when the notes change; returns the notes (stable identity until then). */
export function useHotoNotes(): Record<string, string> {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      void loadHotoNotes();
      return () => { listeners.delete(cb); };
    },
    getHotoNotes,
  );
}
